/**
 * The words in a baby's notes, for the overview's word cloud: how many notes
 * mention each word (a note that says "fussy, fussy" counts once), without
 * filler words or the babies' own names, and with simple plurals folded into
 * the singular ("naps" counts as "nap" when both appear). Pure and tested.
 */
import type { EntryLike } from "./entry-text.ts";

export interface NoteWord {
  word: string;
  /** Notes that mention it. */
  count: number;
  noteIds: string[];
}

/** Common English filler, plus words every baby note has in it. */
const STOP = new Set(
  `a about above after again against all also am an and any are aren't as at be because been
  before being below between both but by can can't cannot could couldn't did didn't do does
  doesn't doing don't down during each few for from further get gets getting got had hadn't has
  hasn't have haven't having he he'd he'll he's her here here's hers herself him himself his how
  how's i i'd i'll i'm i've if in into is isn't it it's its itself just let's like me more most
  much mustn't my myself no nor not now of off on once only or other ought our ours ourselves
  out over own really same she she'd she'll she's should shouldn't so some still such than that
  that's the their theirs them themselves then there there's these they they'd they'll they're
  they've this those through to too under until up very was wasn't we we'd we'll we're we've were
  weren't what what's when when's where where's which while who who's whom why why's will with
  won't would wouldn't you you'd you'll you're you've your yours yourself yourselves
  bit seemed seems seem went today tonight yet lot lots maybe though one two three
  use used using look looks looked make made give gave given back around
  baby babies twins twin pm min mins hr hrs oz`.split(/\s+/),
);

const WORD = /\p{L}+(?:['’]\p{L}+)*/gu;

/** Lowercased words of one note, possessives trimmed ("Ada's" → "ada"). */
function wordsOf(text: string): string[] {
  return (text.toLowerCase().match(WORD) ?? []).map((w) => w.replace(/’/g, "'").replace(/'s$/, ""));
}

export function noteWords(
  entries: readonly EntryLike[],
  babyId: string,
  /** Names and nicknames to leave out — they'd top every cloud. */
  names: readonly string[] = [],
  limit = 40,
): NoteWord[] {
  const skip = new Set(names.flatMap((n) => wordsOf(n)));
  const ids = new Map<string, Set<string>>();
  for (const e of entries) {
    if (e.babyId !== babyId || e.type !== "note" || !e.note) continue;
    for (const w of wordsOf(e.note)) {
      if (w.length < 3 || STOP.has(w) || skip.has(w)) continue;
      if (!ids.has(w)) ids.set(w, new Set());
      ids.get(w)!.add(e.id);
    }
  }
  // Fold "naps" into "nap" when both were written.
  for (const [w, set] of ids) {
    if (!w.endsWith("s") || w.endsWith("ss")) continue;
    const one = ids.get(w.slice(0, -1));
    if (!one) continue;
    for (const id of set) one.add(id);
    ids.delete(w);
  }
  return [...ids]
    .map(([word, set]) => ({ word, count: set.size, noteIds: [...set] }))
    .sort((a, b) => b.count - a.count || a.word.localeCompare(b.word))
    .slice(0, limit);
}

/**
 * Split a note around a cloud word (and its plural or possessive) so the
 * match can be marked; odd indexes are the matches. A scan rather than a
 * lookbehind regex, which older iPad Safari can't parse.
 */
export function splitOnWord(text: string, word: string): string[] {
  const parts: string[] = [];
  let last = 0;
  for (const m of text.matchAll(WORD)) {
    const [w] = wordsOf(m[0]);
    if (w !== word && w !== `${word}s`) continue;
    parts.push(text.slice(last, m.index), m[0]);
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return parts;
}
